import React, { useState } from 'react';
import { toast } from 'sonner';
import { Play } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useUser } from '@/contexts/UserContext';
import { api, errorMessage, fmtInt, timeAgo, useConfigFlag } from './lib';
import { Btn, ErrorState, Page, Panel, Pill, useConfirm } from './ui';

const FLAGS = [
  {
    key: 'beta_mode_enabled',
    title: 'Beta mode',
    on: 'Only beta testers can use the app. Everyone else sees a maintenance screen.',
    off: 'Everyone can use the app.',
    confirmOn: {
      title: 'Turn on beta mode?',
      description: 'Everyone except beta testers will be locked out and see a maintenance screen, paying customers included.',
      confirmLabel: 'Turn on',
      destructive: true,
    },
  },
];

function FlagRow({ flag, confirm }) {
  const { data: value, error, loading, setData, refetch } = useConfigFlag(flag.key);
  const [saving, setSaving] = useState(false);

  const toggle = async (next) => {
    const ask = next ? flag.confirmOn : flag.confirmOff;
    if (ask && !(await confirm(ask))) return;
    setSaving(true);
    setData(next);
    try {
      await api.put(`/config/${flag.key}`, { value: next });
      toast.success(`${flag.title}: ${next ? 'on' : 'off'}`);
    } catch (err) {
      setData(!next);
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 px-4 py-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground">{flag.title}</p>
          {value !== undefined && (
            <Pill tone={flag.key === 'beta_mode_enabled' ? (value ? 'warning' : 'neutral') : (value ? 'success' : 'warning')}>
              {value ? 'On' : 'Off'}
            </Pill>
          )}
        </div>
        {error ? (
          <div className="mt-2"><ErrorState error={error} onRetry={refetch} title="Could not read this setting" /></div>
        ) : (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {value === undefined ? 'Loading…' : value ? flag.on : flag.off}
          </p>
        )}
      </div>
      {value === undefined && loading ? (
        <Skeleton className="h-5 w-9 rounded-full" />
      ) : (
        <Switch
          checked={!!value}
          onCheckedChange={toggle}
          disabled={saving || value === undefined}
          className="mt-0.5 data-[state=checked]:bg-foreground"
          aria-label={flag.title}
        />
      )}
    </div>
  );
}

const JOBS = [
  {
    path: '/admin/resync-trial-status',
    title: 'Fix trial statuses',
    description: 'Moves people whose trial date has passed to Expired, and restores Trial for anyone whose trial is still running. Uses the database only.',
    summary: (r) => `${fmtInt(r.upgraded_to_trial)} set back to Trial · ${fmtInt(r.downgraded_to_expired)} set to Expired`,
  },
  {
    path: '/admin/resync-android-subscriptions',
    title: 'Resync Google Play purchases',
    description: 'Checks every stored Google Play purchase with Google, fixes plans, and records a payment for any trial that has turned into a paid subscription.',
    summary: (r) => `${fmtInt(r.updated_premium)} set to Premium · ${fmtInt(r.updated_trial)} set to Trial · ${fmtInt(r.recorded_paid)} payments recorded · ${fmtInt(r.errors)} errors`,
  },
  {
    path: '/admin/resync-ios-purchases',
    title: 'Resync App Store purchases',
    description: 'Looks up every iOS purchase with Apple to record the price actually paid, and removes access for refunded purchases.',
    summary: (r) => `${fmtInt(r.amount_updated)} amounts updated · ${fmtInt(r.revoked)} refunds removed · ${fmtInt(r.restored_lifetime)} lifetime restored · ${fmtInt(r.not_found)} not found · ${fmtInt(r.errors)} errors`,
  },
];

function JobRow({ job, confirm }) {
  const [state, setState] = useState({ running: false, result: null, error: null, at: null });

  const run = async () => {
    const ok = await confirm({
      title: `${job.title}?`,
      description: `${job.description} This changes customer records.`,
      confirmLabel: 'Run now',
    });
    if (!ok) return;
    setState({ running: true, result: null, error: null, at: null });
    try {
      const result = await api.post(job.path);
      setState({ running: false, result, error: null, at: new Date() });
      toast.success(`${job.title}: done`);
    } catch (err) {
      setState({ running: false, result: null, error: err, at: new Date() });
      toast.error(errorMessage(err));
    }
  };

  const errors = state.result?.details?.errors || [];
  return (
    <div className="px-4 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{job.title}</p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{job.description}</p>
        </div>
        <Btn variant="outline" size="sm" onClick={run} disabled={state.running} className="shrink-0">
          <Play className="h-3.5 w-3.5" />{state.running ? 'Running…' : 'Run'}
        </Btn>
      </div>
      {state.result && (
        <div className="mt-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-foreground">
          <p>{job.summary(state.result)}</p>
          <p className="mt-0.5 text-muted-foreground">Finished {timeAgo(state.at)}</p>
          {errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-amber-700">Show {errors.length} error{errors.length === 1 ? '' : 's'}</summary>
              <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto">
                {errors.map((e, i) => (
                  <li key={`${e.user_id}-${i}`} className="break-all"><span className="font-mono">{e.user_id}</span>: {e.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {state.error && <div className="mt-3"><ErrorState error={state.error} title={`${job.title} failed`} /></div>}
    </div>
  );
}

export default function SettingsPanel() {
  const [confirm, confirmDialog] = useConfirm();
  const { user } = useUser();
  return (
    <Page title="Settings" description="Switches that change the app for everyone, and maintenance jobs for subscriptions.">
      <Panel title="App" description="These take effect immediately for every user." flush>
        <div className="divide-y divide-border">
          {FLAGS.map((flag) => <FlagRow key={flag.key} flag={flag} confirm={confirm} />)}
        </div>
      </Panel>
      <Panel
        title="Subscription maintenance"
        description="Run these when plans look wrong. Fix trial statuses first, then the store resyncs."
        flush
      >
        <div className="divide-y divide-border">
          {JOBS.map((job) => <JobRow key={job.path} job={job} confirm={confirm} />)}
        </div>
      </Panel>
      <p className="text-xs text-muted-foreground">Signed in as {user?.email}.</p>
      {confirmDialog}
    </Page>
  );
}
